import { useEffect, useMemo, useState } from "react";
import {
  getBroadcastAddress,
  getNetworkAddress,
  useAnketaAuth,
  useApi,
  useToast,
  Vpc,
} from "../../../../";
import {
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiSpacer,
  EuiSwitch,
} from "@elastic/eui";
import { ComplexQuestionEditorProps } from "../../../";
import { useAnketa } from "../../context_provider";
import { AnketaResponse } from "../../../../api/types";
import { useParams } from "react-router";
import { useAnketaI18n } from "src/internationalization";

export const ComplexQuestionNetworkEditor = ({
  question,
  setValue,
  invalid,
}: ComplexQuestionEditorProps): JSX.Element => {
  const { keycloak } = useAnketaAuth();
  const ctx = useAnketa();
  const { Get } = useApi();
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();
  const params = useParams();
  const { catalogId, questionnaireId } = params;

  const [vpcs, setVpcs] = useState<Vpc[]>([]);
  const [tenants, setTenants] = useState<number[]>([]);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [testStage, setTestStage] = useState<boolean>(false);

  const [selectedCidr, setSelectedCidr] =
    useState<EuiComboBoxOptionOption<Vpc> | null>(null);
  const [selectedTenantId, setSelectedTenantId] =
    useState<EuiComboBoxOptionOption<number> | null>(null);

  const anketaUrl = useMemo(() => {
    return `https://anketa.exanio.cloud/questionnaires/${catalogId}/${questionnaireId}`;
  }, [catalogId, questionnaireId]);

  const canFetch = useMemo<boolean>(() => {
    return typeof keycloak?.token === "string";
  }, [keycloak]);

  const fetchFreeNetworks = async (): Promise<void> => {
    setIsLoaded(false);
    try {
      const { data } = await Get<
        AnketaResponse<{ vpcs: Vpc[]; tenantIds: number[] }>
      >({
        path: `v2/gitlab/network/free?top=10&env=${testStage ? "test" : "prod"}`,
        responseType: "json",
        token: keycloak.token,
      });
      setVpcs(data.data.vpcs || []);
      setTenants(data.data.tenantIds || []);
    } catch (error) {
      console.error("Error fetching free networks:", error);
      addToast({
        title: translate("anketa.questionnaire.editor.network.error.title", {
          defaultMessage: "Network error",
        }),
        text: translate("anketa.questionnaire.editor.network.error.text", {
          defaultMessage: "Free networks could not be loaded.",
        }),
        color: "danger",
      });
      setVpcs([]);
      setTenants([]);
    } finally {
      setIsLoaded(true);
    }
  };

  const cidrOptions = useMemo<EuiComboBoxOptionOption<Vpc>[]>(() => {
    return vpcs.map((vpc) => ({ label: vpc.cidr, value: vpc }));
  }, [vpcs]);

  const tenantIdOptions = useMemo<EuiComboBoxOptionOption<number>[]>(() => {
    return tenants.map((id) => ({ label: String(id), value: id }));
  }, [tenants]);

  const commitValue = (
    cidrOpt: EuiComboBoxOptionOption<Vpc> | null,
    tenantOpt: EuiComboBoxOptionOption<number> | null,
  ) => {
    if (!cidrOpt?.value || tenantOpt?.value == null) return;
    const merged: Vpc = {
      name: "",
      owner: "",
      project: "",
      stage: "",
      provider: "",
      isDeployed: false,
      netBox: "",
      anketa: anketaUrl,
      tenantId: tenantOpt.value,
      cidr: cidrOpt.value.cidr,
      first: cidrOpt.value.first,
      last: cidrOpt.value.last,
      bits: cidrOpt.value.bits,
    };
    setValue(JSON.stringify(merged));
  };

  const onCidrChange = (selected: EuiComboBoxOptionOption<Vpc>[]) => {
    const opt = selected[0] ?? null;
    setSelectedCidr(opt);
    commitValue(opt, selectedTenantId);
  };

  const onTenantIdChange = (selected: EuiComboBoxOptionOption<number>[]) => {
    const opt = selected[0] ?? null;
    setSelectedTenantId(opt);
    commitValue(selectedCidr, opt);
  };

  const onCreateTenantId = (input: string) => {
    const parsed = parseInt(input);
    if (input.trim() === "" || isNaN(parsed)) return;
    const newOpt: EuiComboBoxOptionOption<number> = {
      label: String(parsed),
      value: parsed,
    };
    setSelectedTenantId(newOpt);
    commitValue(selectedCidr, newOpt);
  };

  const onCreateNetwork = (input: string) => {
    if (input.trim() === "") return;

    const currentTenantId = selectedTenantId?.value ?? tenants[0] ?? 0;

    const newVpc: Vpc = {
      name: "",
      owner: "",
      project: "",
      stage: "",
      provider: "",
      isDeployed: false,
      netBox: "",
      anketa: anketaUrl,
      tenantId: currentTenantId,
      cidr: input,
      first: getNetworkAddress(input),
      last: getBroadcastAddress(input),
      bits: input.split("/")[1] ? parseInt(input.split("/")[1]) : 0,
    };

    const newOpt: EuiComboBoxOptionOption<Vpc> = {
      label: input,
      value: newVpc,
    };

    setSelectedCidr(newOpt);
    commitValue(newOpt, selectedTenantId);
  };

  useEffect(() => {
    if (canFetch) fetchFreeNetworks();
  }, [canFetch, testStage]);

  useEffect(() => {
    if (!isLoaded || vpcs.length === 0) return;

    const savedStr = question.fact.getStrValue(ctx.i18nContext);
    if (savedStr) {
      try {
        const parsed: Vpc = JSON.parse(savedStr);
        setSelectedCidr({ label: parsed.cidr, value: parsed });
        setSelectedTenantId({
          label: String(parsed.tenantId),
          value: parsed.tenantId,
        });
      } catch {}
    } else {
      const first = vpcs[0];
      const firstTenantId = tenants[0] ?? first.tenantId;
      const cidrOpt: EuiComboBoxOptionOption<Vpc> = {
        label: first.cidr,
        value: first,
      };
      const tenantOpt: EuiComboBoxOptionOption<number> = {
        label: String(firstTenantId),
        value: firstTenantId,
      };
      setSelectedCidr(cidrOpt);
      setSelectedTenantId(tenantOpt);
      commitValue(cidrOpt, tenantOpt);
    }
  }, [isLoaded]);

  return (
    <EuiFlexItem grow style={{ minWidth: "300px" }}>
      <EuiFlexGroup alignItems="flexStart" gutterSize="m">
        {!question.readonly && (
          <EuiFlexItem grow={false} style={{ paddingTop: 28 }}>
            <EuiSwitch
              label={testStage ? "Test" : "Prod"}
              checked={testStage}
              onChange={() => setTestStage((prev) => !prev)}
            />
          </EuiFlexItem>
        )}

        <EuiFlexItem>
          <EuiFormRow label="CIDR">
            <EuiComboBox
              placeholder={translate(
                "anketa.questionnaire.editor.network.cidr.placeholder",
                { defaultMessage: "Choose network" },
              )}
              options={cidrOptions}
              selectedOptions={selectedCidr ? [selectedCidr] : []}
              onChange={onCidrChange}
              isClearable={true}
              isLoading={!isLoaded && canFetch}
              isDisabled={question.readonly}
              isInvalid={invalid}
              singleSelection={{ asPlainText: true }}
              onCreateOption={onCreateNetwork}
            />
          </EuiFormRow>
        </EuiFlexItem>

        <EuiFlexItem grow={false} style={{ minWidth: "130px" }}>
          <EuiFormRow label="Tenant ID">
            <EuiComboBox
              placeholder={translate(
                "anketa.questionnaire.editor.network.tenant.placeholder",
                { defaultMessage: "Choose ID" },
              )}
              options={tenantIdOptions}
              selectedOptions={selectedTenantId ? [selectedTenantId] : []}
              onChange={onTenantIdChange}
              isClearable={true}
              isLoading={!isLoaded && canFetch}
              isDisabled={question.readonly}
              isInvalid={invalid}
              singleSelection={{ asPlainText: true }}
              onCreateOption={onCreateTenantId}
            />
          </EuiFormRow>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="l" />
    </EuiFlexItem>
  );
};
